import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { InstallProvider } from "./context/InstallContext";
import { InstallHelpDialog } from "./components/InstallBanner";
import AppShell from "./components/AppShell";
import { Loader } from "./components/bits";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Pending from "./pages/Pending";
import Dashboard from "./pages/Dashboard";
import Feed from "./pages/Feed";
import Directory from "./pages/Directory";
import MemberProfile from "./pages/MemberProfile";
import Help from "./pages/Help";
import HelpDetail from "./pages/HelpDetail";
import Referrals from "./pages/Referrals";
import Events from "./pages/Events";
import CityConnect from "./pages/CityConnect";
import Admin from "./pages/Admin";
import MyProfile from "./pages/MyProfile";
import Contribute from "./pages/Contribute";
import ComingSoon from "./pages/ComingSoon";
import AdminLogin from "./pages/AdminLogin";
import ContributionsAdmin from "./pages/ContributionsAdmin";

// Alumni network (login, approvals, feed, directory...) is built but hidden for now.
const NETWORK_ENABLED = false;

const Gate = ({ need, children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader full />;
  if (!user) return <Navigate to="/login" replace />;
  if (need === "auth") return children;
  if (!user.profile_complete) return <Navigate to="/onboarding" replace />;
  if (user.status !== "approved") return <Navigate to="/pending" replace />;
  if (need === "admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader full />;
  if (user) return <Navigate to={user.role === "admin" && location.search.includes("admin=1") ? "/admin" : "/dashboard"} replace />;
  return children;
};

const member = (el) => <Gate need="member">{el}</Gate>;

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader full />;
  if (!user || user.role !== "admin") return <Navigate to="/adminlogin" replace />;
  return children;
};

const AdminLoginRoute = () => {
  const { user } = useAuth();
  if (user === null) return <Loader full />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  return <AdminLogin />;
};

function AppRouter() {
  const location = useLocation();
  if (NETWORK_ENABLED && location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {!NETWORK_ENABLED && <Route path="/" element={<ComingSoon />} />}
      <Route path="/filltheform" element={<Contribute />} />
      <Route path="/adminlogin" element={<AdminLoginRoute />} />
      <Route path="/admin/login" element={<Navigate to="/adminlogin" replace />} />
      <Route path="/admin" element={<AdminOnly><ContributionsAdmin /></AdminOnly>} />
      {NETWORK_ENABLED && (
        <>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/onboarding" element={<Gate need="auth"><Onboarding /></Gate>} />
          <Route path="/pending" element={<Gate need="auth"><Pending /></Gate>} />
          <Route path="/dashboard" element={member(<Dashboard />)} />
          <Route path="/feed" element={member(<Feed />)} />
          <Route path="/directory" element={member(<Directory />)} />
          <Route path="/members/:id" element={member(<MemberProfile />)} />
          <Route path="/help" element={member(<Help />)} />
          <Route path="/help/:id" element={member(<HelpDetail />)} />
          <Route path="/business" element={member(<Referrals />)} />
          <Route path="/events" element={member(<Events />)} />
          <Route path="/city" element={member(<CityConnect />)} />
          <Route path="/profile" element={member(<MyProfile />)} />
          <Route path="/network-admin" element={<Gate need="admin"><Admin /></Gate>} />
        </>
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <InstallProvider>
          <AppRouter />
          <InstallHelpDialog />
          <Toaster position="top-center" richColors />
        </InstallProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
